/**
 * Best-effort recall of recent analytics session IDs for consent revoke (P2-06).
 */
const STORAGE_PREFIX = 'analytics:lastSessions:';
const MAX_STORED_SESSIONS = 5;

interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function getDefaultSessionStorage(): SessionStorageLike | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function storageKey(tenantCode: string): string {
  return `${STORAGE_PREFIX}${tenantCode}`;
}

export function recordAnalyticsSessionId(
  tenantCode: string,
  sessionId: string,
  storage: SessionStorageLike | null = getDefaultSessionStorage(),
): void {
  if (storage === null || sessionId.length === 0) {
    return;
  }
  const existingRaw = storage.getItem(storageKey(tenantCode));
  const existing: string[] =
    existingRaw !== null && existingRaw.length > 0
      ? (JSON.parse(existingRaw) as string[])
      : [];
  const next = [sessionId, ...existing.filter((id) => id !== sessionId)].slice(
    0,
    MAX_STORED_SESSIONS,
  );
  storage.setItem(storageKey(tenantCode), JSON.stringify(next));
}

export function readStoredAnalyticsSessionIds(
  tenantCode: string,
  storage: SessionStorageLike | null = getDefaultSessionStorage(),
): readonly string[] {
  if (storage === null) {
    return [];
  }
  const raw = storage.getItem(storageKey(tenantCode));
  if (raw === null || raw.length === 0) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((id): id is string => typeof id === 'string' && id.length > 0);
  } catch {
    return [];
  }
}

export function clearStoredAnalyticsSessionIds(
  tenantCode: string,
  storage: SessionStorageLike | null = getDefaultSessionStorage(),
): void {
  if (storage === null) {
    return;
  }
  storage.removeItem(storageKey(tenantCode));
}
